import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import {
  formatImeiLockoutCountdown,
  getActiveSimAttemptState,
  getActiveImeiAttemptState,
  type DeviceVerifyFieldKey,
  type ImeiVerificationAttemptState,
} from "./imei-verification-attempts-storage";

/**
 * Re-reads IMEI attempt / lockout from storage on mount, timer tick, tab focus,
 * and bfcache restore so block UI stays correct after refresh.
 */
export function useImeiPurchaseLockoutUi(
  scopeKey: string,
  field: DeviceVerifyFieldKey = "imei"
) {
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [syncGen, setSyncGen] = useState(0);

  useLayoutEffect(() => {
    setSyncGen((g) => g + 1);
  }, [scopeKey]);

  useEffect(() => {
    const id = window.setInterval(() => setNowTick(Date.now()), 1000);
    const bump = () => setSyncGen((g) => g + 1);
    window.addEventListener("pageshow", bump);
    document.addEventListener("visibilitychange", bump);
    window.addEventListener("focus", bump);
    window.addEventListener("storage", bump);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("pageshow", bump);
      document.removeEventListener("visibilitychange", bump);
      window.removeEventListener("focus", bump);
      window.removeEventListener("storage", bump);
    };
  }, [scopeKey]);

  void nowTick;
  void syncGen;

  const attemptState: ImeiVerificationAttemptState =
    field === "sim"
      ? getActiveSimAttemptState(scopeKey)
      : getActiveImeiAttemptState(scopeKey);
  const imeiLockoutUntil = attemptState.lockoutUntil;
  const isImeiFieldLocked =
    imeiLockoutUntil != null && Date.now() < imeiLockoutUntil;
  const imeiLockoutRemainingSec =
    isImeiFieldLocked && imeiLockoutUntil
      ? Math.max(0, Math.ceil((imeiLockoutUntil - Date.now()) / 1000))
      : 0;

  const bumpLockoutSync = useCallback(() => {
    setSyncGen((g) => g + 1);
  }, []);

  return {
    imeiAttemptState: attemptState,
    isImeiFieldLocked,
    imeiLockoutRemainingSec,
    formatImeiLockoutCountdown,
    bumpLockoutSync,
  };
}
